import { consola } from 'consola'
import { useModalStore , useAlertStore , modalStateEnum } from '@shared/model'


import { getPaymentUrl, type Order } from '@entities/order'

/**
 * Возвращает ссылку на оплату заказа (если есть).
 * Совместимо с разными форматами данных (payment_url/confirmation_url и т.п.).
 */
export function getOrderPaymentUrl(order: Order | null, modalData?: any): string | null {
  return getPaymentUrl(order, modalData)
}

/**
 * Открывает модальное окно с деталями заказа (ModalThankYou)
 * @param order - заказ для отображения
 */
export function openOrderDetailsModal(order: Order | null): void {
  if (!order?.id) {
    consola.warn('No order ID available for modal')
    return
  }
  
  consola.debug('Opening ModalThankYou with order:', order.id)
  const modalStore = useModalStore()
  modalStore.open(modalStateEnum.ModalThankYou, { order, orderId: order.id })
}

/**
 * Открывает модальное окно TBusiness виджета
 */
export function openTBusinessModal(): void {
  consola.debug('Opening TBusiness widget modal')
  const modalStore = useModalStore()
  modalStore.open(modalStateEnum.TBusinessWidget)
}

/**
 * Открывает модальное окно YooKassa виджета
 */
export function openYooKassaModal(): void {
  consola.debug('Opening YooKassa widget modal')
  const modalStore = useModalStore()
  modalStore.open(modalStateEnum.YooKassaWidget)
}

/**
 * Открывает модальное окно Альфа-Банк виджета.
 *
 * @param confirmationUrl ссылка на платёжную страницу банка (formUrl). Без неё виджету
 * остаётся только payment store, а там ссылки может не быть — например при повторе оплаты.
 */
export function openAlfaBankModal(confirmationUrl?: string | null): void {
  consola.debug('Opening Alfa Bank widget modal')
  const modalStore = useModalStore()
  modalStore.open(modalStateEnum.AlfaBankWidget, confirmationUrl ? { confirmationUrl } : undefined)
}

/**
 * Закрывает все открытые модальные окна
 */
export function closeAllModals(): void {
  consola.debug('Closing all modals')
  const modalStore = useModalStore()
  modalStore.close()
}

/**
 * Показывает уведомление об ошибке
 * @param message - текст ошибки
 */
export function showErrorAlert(message: string): void {
  consola.error('Error alert:', message)
  const alertStore = useAlertStore()
  alertStore.error(message)
}

/**
 * Показывает информационное уведомление
 * @param message - текст уведомления
 */
export function showInfoAlert(message: string): void {
  consola.debug('Info alert:', message)
  const alertStore = useAlertStore()
  alertStore.info(message)
}

/**
 * Показывает предупреждающее уведомление
 * @param message - текст предупреждения
 */
export function showWarningAlert(message: string): void {
  consola.warn('Warning alert:', message)
  const alertStore = useAlertStore()
  alertStore.warn(message)
}

/**
 * Показывает уведомление об успехе
 * @param message - текст успешного действия
 */
export function showSuccessAlert(message: string): void {
  consola.debug('Success alert:', message)
  const alertStore = useAlertStore()
  alertStore.success(message)
}

/**
 * Обрабатывает ошибку загрузки деталей заказа
 */
export function handleOrderDetailsLoadError(): void {
  showErrorAlert('Не удалось загрузить информацию о заказе')
}

/**
 * Обрабатывает ошибку инициализации повторной оплаты
 */
export function handleRetryPaymentError(): void {
  showErrorAlert('Не удалось открыть форму оплаты')
}

/**
 * Обрабатывает ошибку загрузки деталей заказа из API
 */
export function handleOrderDetailsAPIError(): void {
  showErrorAlert('Не удалось загрузить детали заказа')
}

/**
 * Показывает уведомление об истечении времени оплаты
 */
export function showPaymentTimeExpiredAlert(): void {
  showWarningAlert('Время для оплаты истекло')
}

/**
 * Показывает уведомление о переходе к оплате
 */
export function showGoingToPaymentAlert(): void {
  showInfoAlert('Переходим к оплате заказа')
}

/**
 * Логирует действие с модальным окном
 * @param action - описание действия
 * @param modalType - тип модального окна (опционально)
 */
export function logModalAction(action: string, modalType?: string): void {
  const modalInfo = modalType ? ` (${modalType})` : ''
  consola.debug(`🔲 Modal action: ${action}${modalInfo}`)
}

/**
 * Логирует действие с уведомлением
 * @param type - тип уведомления
 * @param message - текст уведомления
 */
export function logAlertAction(type: string, message: string): void {
  consola.debug(`🔔 Alert ${type}: ${message}`)
}